/**
 * ARKO-Thread-localStorage, ohne den Chat-Workspace zu mounten.
 * Gleiche Prefix-Konvention wie PortalArkoChatWorkspace (Handshake nach Diktat).
 */

import type { Message } from '../InlineChat';

export const ARKO_THREADS_STORAGE_PREFIX = 'arkorbit_portal_arko_threads_v1__';

type StoredThread = {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: number;
};

type StoredWorkspace = {
  version: 1;
  activeThreadIds: string[];
  activeId: string;
  threads: Record<string, StoredThread>;
  archived: Record<string, StoredThread>;
};

function storageKey(customerId: string): string {
  return `${ARKO_THREADS_STORAGE_PREFIX}${customerId}`;
}

function readWorkspace(customerId: string): StoredWorkspace | null {
  try {
    const raw = localStorage.getItem(storageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredWorkspace>;
    if (parsed?.version !== 1 || !parsed.threads || typeof parsed.activeId !== 'string') {
      return null;
    }
    return parsed as StoredWorkspace;
  } catch {
    return null;
  }
}

function writeWorkspace(customerId: string, state: StoredWorkspace): void {
  try {
    localStorage.setItem(storageKey(customerId), JSON.stringify(state));
  } catch {
    /* privater Modus */
  }
}

export function peekActiveArkoThreadId(customerId: string): string | null {
  const state = readWorkspace(customerId);
  if (!state) return null;
  if (state.threads[state.activeId]) return state.activeId;
  const first = state.activeThreadIds.find((id) => state.threads[id]);
  return first ?? null;
}

export function ensureStoredActiveArkoThreadId(customerId: string): string {
  const existing = peekActiveArkoThreadId(customerId);
  if (existing) return existing;
  const id = `diktat_${Date.now().toString(36)}`;
  const thread: StoredThread = {
    id,
    title: 'Diktat',
    messages: [],
    updatedAt: Date.now(),
  };
  writeWorkspace(customerId, {
    version: 1,
    activeThreadIds: [id],
    activeId: id,
    threads: { [id]: thread },
    archived: {},
  });
  return id;
}

export function readStoredArkoThreadMessages(
  customerId: string,
  threadId: string,
): Message[] | undefined {
  const state = readWorkspace(customerId);
  const thread = state?.threads[threadId];
  return thread?.messages;
}

export function appendStoredArkoThreadMessages(
  customerId: string,
  threadId: string,
  updater: (prev: Message[]) => Message[],
): void {
  const state = readWorkspace(customerId);
  if (!state?.threads[threadId]) return;
  const prev = state.threads[threadId];
  const messages = updater(prev.messages ?? []);
  writeWorkspace(customerId, {
    ...state,
    threads: {
      ...state.threads,
      [threadId]: { ...prev, messages, updatedAt: Date.now() },
    },
  });
}
